import { Button } from "@iluvatar/ui-kit";
import { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  Navigate,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";

import ServiceWorkerPrompt from "@/components/ServiceWorkerPrompt";
import { useAuthenticationContext } from "@/contexts/authentication/authentication.context";
import { PublicAuthOutlet } from "@/layout/public-auth-outlet";
import { isPublicPath } from "@/utilities/navigation";
import { lazy, Suspense, useState } from "react";
const PushNotificationSync = lazy(() =>
  import("@/components/PushNotificationSync").then((module) => ({
    default: module.PushNotificationSync,
  })),
);

import { getAccountSession } from "@/services/account-session";
import { getAuthRuntime } from "@/services/firebase-auth.runtime";

import { waitForAuthReady } from "@/services/auth-session";

function RootComponent() {
  const { initialized, user, authToken, reload, logout } =
    useAuthenticationContext();
  const [sessionError, setSessionError] = useState<string>();
  const pathname = useRouterState({
    select: (state) => state.location.pathname.replace(/\/+$/, "") || "/",
  });
  if (user && !authToken) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
        <p role="alert">
          {sessionError ?? "Could not prepare your account. Please try again."}
        </p>
        <Button
          variant="outline"
          onClick={() => {
            void reload().catch((error: unknown) =>
              setSessionError(
                error instanceof Error
                  ? error.message
                  : "Could not prepare your account.",
              ),
            );
          }}
        >
          Retry
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            void logout().catch((error: unknown) =>
              setSessionError(
                error instanceof Error ? error.message : "Could not sign out.",
              ),
            );
          }}
        >
          Sign out
        </Button>
      </div>
    );
  }
  if (
    authToken?.claims.setupRequired === true &&
    pathname !== "/account-setup"
  ) {
    return <Navigate to="/account-setup" replace />;
  }
  return (
    <>
      <PublicAuthOutlet />
      <ServiceWorkerPrompt />
      {initialized && !authToken?.claims.setupRequired && (
        <Suspense fallback={null}>
          <PushNotificationSync />
        </Suspense>
      )}
      {/* <ReactQueryDevtools buttonPosition="top-right" /> */}
      {import.meta.env.MODE == "development" && (
        <TanStackRouterDevtools position="bottom-right" />
      )}
    </>
  );
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: RootComponent,
  beforeLoad: async function authBeforeLoad({ location }) {
    if (isPublicPath(location.pathname)) {
      return {};
    }

    await waitForAuthReady();
    const { auth } = await getAuthRuntime();
    if (!auth.currentUser) {
      throw redirect({
        to: "/login",
        search: {
          redirectTo: location.href,
        },
      });
    }

    const { token } = await getAccountSession(auth.currentUser);
    const setupPage =
      location.pathname.replace(/\/+$/, "") === "/account-setup";
    if (token.claims.setupRequired && !setupPage) {
      throw redirect({ to: "/account-setup", replace: true });
    }
    if (setupPage && !token.claims.setupRequired) {
      throw redirect({ to: "/", replace: true });
    }
    return {};
  },
});
